import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { DatabaseAccessGrantDecisionBody, DatabaseBranchCreateBody, DomainCreateBody, SecretSetBody, VariableSetBody } from "./types";

/* Domains ---------------------------------------------------------------- */

export function useDomains(project: string, environment: string) {
  return useQuery({
    queryKey: qk.domains(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/domains", {
          params: { path: { project, environment } },
        }),
      ),
    enabled: !!project && !!environment,
  });
}

/** Poll while verifying: pass refetchInterval (e.g. 5000) until checks are all ok. */
export function useDomain(project: string, environment: string, domain: string, opts: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: qk.domain(project, environment, domain),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/domains/{domain}", {
          params: { path: { project, environment, domain } },
        }),
      ),
    refetchInterval: opts.refetchInterval ?? false,
    enabled: !!project && !!environment && !!domain,
  });
}

export function useAddDomain(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DomainCreateBody) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/domains", {
          params: { path: { project, environment }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.domains(project, environment) }),
  });
}

export function useVerifyDomain(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/domains/{domain}/verify", {
          params: { path: { project, environment, domain }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.domains(project, environment) }),
  });
}

/* Databases -------------------------------------------------------------- */

export function useDatabases(project: string, environment: string) {
  return useQuery({
    queryKey: qk.databases(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/databases", {
          params: { path: { project, environment } },
        }),
      ),
    enabled: !!project && !!environment,
  });
}

export function useDatabase(project: string, environment: string, database: string) {
  return useQuery({
    queryKey: qk.database(project, environment, database),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/databases/{database}", {
          params: { path: { project, environment, database } },
        }),
      ),
    enabled: !!project && !!environment && !!database,
  });
}

export function useDatabaseBranches(project: string, environment: string, database: string) {
  return useQuery({
    queryKey: qk.databaseBranches(project, environment, database),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/databases/{database}/branches", {
          params: { path: { project, environment, database } },
        }),
      ),
    enabled: !!project && !!environment && !!database,
  });
}

export function useCreateDatabaseBranch(project: string, environment: string, database: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DatabaseBranchCreateBody) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/databases/{database}/branches", {
          params: { path: { project, environment, database }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.databaseBranches(project, environment, database) }),
  });
}

export function useDatabaseBackups(project: string, environment: string, database: string) {
  return useQuery({
    queryKey: qk.databaseBackups(project, environment, database),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/databases/{database}/backups", {
          params: { path: { project, environment, database } },
        }),
      ),
    enabled: !!project && !!environment && !!database,
  });
}

/* Database access grants (guv db proxy / connect) ------------------------ */

export function useDatabaseAccessGrant(project: string, environment: string, database: string, grant: string) {
  return useQuery({
    queryKey: qk.databaseAccessGrant(project, environment, database, grant),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/databases/{database}/access-grants/{grant}", {
          params: { path: { project, environment, database, grant } },
        }),
      ),
    enabled: !!project && !!environment && !!database && !!grant,
    retry: false,
  });
}

export function useApproveDatabaseAccessGrant(project: string, environment: string, database: string, grant: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DatabaseAccessGrantDecisionBody) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/databases/{database}/access-grants/{grant}/approve", {
          params: { path: { project, environment, database, grant }, header: idem() },
          body,
        }),
      ),
    onSuccess: (g) => qc.setQueryData(qk.databaseAccessGrant(project, environment, database, grant), g),
  });
}

export function useDenyDatabaseAccessGrant(project: string, environment: string, database: string, grant: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/databases/{database}/access-grants/{grant}/deny", {
          params: { path: { project, environment, database, grant }, header: idem() },
        }),
      ),
    onSuccess: (g) => qc.setQueryData(qk.databaseAccessGrant(project, environment, database, grant), g),
  });
}

/* Variables and secrets -------------------------------------------------- */

export function useVariables(project: string, environment: string) {
  return useQuery({
    queryKey: qk.variables(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/variables", {
          params: { path: { project, environment } },
        }),
      ),
    enabled: !!project && !!environment,
  });
}

export function useSetVariables(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VariableSetBody) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/variables", {
          params: { path: { project, environment }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variables(project, environment) }),
  });
}

/** Secrets are write-only: metadata only, values are never returned. */
export function useSecrets(project: string, environment: string) {
  return useQuery({
    queryKey: qk.secrets(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/secrets", {
          params: { path: { project, environment } },
        }),
      ),
    enabled: !!project && !!environment,
  });
}

export function useSetSecret(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SecretSetBody) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/secrets", {
          params: { path: { project, environment }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.secrets(project, environment) }),
  });
}
